import { notify, useConfirmModal } from '@affine/component';
import { useTrashDoc } from '@affine/core/components/hooks/affine/use-trash-doc';
import { useTrashFolderToast } from '@affine/core/components/hooks/affine/use-trash-folder-toast';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { DocsService } from '@affine/core/modules/doc';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  FolderApiService,
  moveFolderToTrashLocalFirst as moveFolderToTrashLocalFirstAction,
  type NavigationPanelFolderVisibility,
  PermissionLifecycleOperation,
  PermissionPreflightTargetKind,
} from '@affine/core/modules/folder-api';
import {
  AccessibleFolderCacheStore,
  FolderStore,
} from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { createElement, useCallback } from 'react';

import type { NavigationPanelTreeSelectionItem } from './context';
import { filterNavigationPanelTrashableSelectionItems } from './selection-trash-capability';

function getFolderVisibility(item: NavigationPanelTreeSelectionItem) {
  return item.from?.at === 'navigation-panel:private:folder-node'
    ? 'private'
    : 'public';
}

export function useNavigationPanelTrashActions() {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderApiService = useService(FolderApiService);
  const folderStore = useService(FolderStore);
  const trashDoc = useTrashDoc();
  const showFolderTrashToast = useTrashFolderToast();
  const { openConfirmModal } = useConfirmModal();
  const isCloudWorkspace = workspace.flavour !== 'local';

  const moveFolderToTrashLocalFirst = useCallback(
    (
      folderId: string,
      canTrashFolder: boolean,
      visibility: NavigationPanelFolderVisibility = 'public',
      options: { skipPreflight?: boolean } = {}
    ) => {
      return moveFolderToTrashLocalFirstAction({
        accessibleDocCache,
        accessibleFolderCache,
        folderApiService,
        folderId,
        folderStore,
        canTrashFolder,
        canUseYjsFolderRoute: !isCloudWorkspace,
        canUseServerDestructiveLifecycle: () =>
          !isCloudWorkspace || connectivityStatus.backendAvailable,
        workspaceId: workspace.id,
        skipPreflight: options.skipPreflight === true,
        notifyConnectionRequired: () => {
          notify.error({
            title: t['com.streamlain.settings.connection-required.title'](),
            message:
              t['com.streamlain.settings.connection-required.description'](),
          });
        },
        notifyMoveFailed: () => {
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
          });
        },
        notifyRestoreFailed: () => {
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.restoreFailed'](),
          });
        },
        showFolderTrashToast,
        visibility,
        logError: console.error,
      });
    },
    [
      accessibleDocCache,
      accessibleFolderCache,
      connectivityStatus.backendAvailable,
      folderApiService,
      folderStore,
      isCloudWorkspace,
      showFolderTrashToast,
      t,
      workspace.id,
    ]
  );

  const trashNavigationPanelItems = useCallback(
    async (items: NavigationPanelTreeSelectionItem[]) => {
      const trashableItems =
        filterNavigationPanelTrashableSelectionItems(items);
      const runTrash = async (
        itemsToTrash: NavigationPanelTreeSelectionItem[]
      ) => {
        for (const item of itemsToTrash) {
          if (item.entity.type === 'folder') {
            const folderTrash = item.folderTrash;
            await moveFolderToTrashLocalFirst(
              item.entity.id,
              folderTrash?.canTrashFolder ?? false,
              getFolderVisibility(item),
              { skipPreflight: isCloudWorkspace }
            );
            continue;
          }

          const docRecord = docsService.list.doc$(item.entity.id).value;
          if (docRecord) {
            await trashDoc(docRecord);
          }
        }
      };
      const preflightTargets = trashableItems.map(item => ({
        kind:
          item.entity.type === 'folder'
            ? PermissionPreflightTargetKind.FOLDER
            : PermissionPreflightTargetKind.DOC,
        id: item.entity.id,
      }));
      if (isCloudWorkspace && preflightTargets.length > 0) {
        const preflight = await folderApiService.preflightPermissionLifecycle({
          workspaceId: workspace.id,
          operation: PermissionLifecycleOperation.FOLDER_TRASH,
          targets: preflightTargets,
        });
        if (!preflight.allowed) {
          const visibleBlockedObjects = preflight.blockedObjects;
          const hiddenBlockedCount = Math.max(
            preflight.totalBlockedCount - visibleBlockedObjects.length,
            0
          );
          const blockedTargetIds = new Set(preflight.blockedTargetIds);
          const allowedItems = trashableItems.filter(
            item => !blockedTargetIds.has(item.entity.id)
          );
          return await new Promise<boolean>(resolve => {
            openConfirmModal(
              {
                title:
                  allowedItems.length > 0
                    ? t[
                        'com.streamlain.navigation.selection.deleteBlocked.title'
                      ]()
                    : t[
                        'com.streamlain.navigation.selection.deleteBlocked.all.title'
                      ](),
                description:
                  allowedItems.length > 0
                    ? t[
                        'com.streamlain.navigation.selection.deleteBlocked.description'
                      ]()
                    : t[
                        'com.streamlain.navigation.selection.deleteBlocked.all.description'
                      ](),
                children: createElement(
                  'div',
                  {
                    style: {
                      display: 'grid',
                      gap: 8,
                      maxHeight: 220,
                      overflow: 'auto',
                    },
                  },
                  [
                    ...visibleBlockedObjects.map((item, index) =>
                      createElement(
                        'div',
                        { key: `${item.kind}:${item.id}:${index}` },
                        t[
                          'com.streamlain.navigation.selection.deleteBlocked.item'
                        ]({
                          name: item.name?.trim() || item.id,
                        })
                      )
                    ),
                    hiddenBlockedCount > 0
                      ? createElement(
                          'div',
                          { key: 'hidden-blocked-count' },
                          t[
                            'com.streamlain.navigation.selection.deleteBlocked.more'
                          ]({ count: hiddenBlockedCount.toString() })
                        )
                      : null,
                  ].filter(Boolean)
                ),
                confirmText:
                  allowedItems.length > 0
                    ? t[
                        'com.streamlain.navigation.selection.deleteBlocked.continue'
                      ]()
                    : t['OK'](),
                cancelText: t['Cancel'](),
                confirmButtonOptions:
                  allowedItems.length > 0
                    ? {
                        variant: 'error',
                      }
                    : undefined,
                onCancel:
                  allowedItems.length > 0
                    ? () => {
                        resolve(false);
                      }
                    : false,
                onConfirm: async () => {
                  if (allowedItems.length > 0) {
                    await runTrash(allowedItems);
                  }
                  resolve(true);
                },
              },
              { autoClose: true }
            );
          });
        }
      }

      await runTrash(trashableItems);
      return true;
    },
    [
      docsService.list,
      folderApiService,
      isCloudWorkspace,
      moveFolderToTrashLocalFirst,
      openConfirmModal,
      t,
      trashDoc,
      workspace.id,
    ]
  );

  return {
    moveFolderToTrashLocalFirst,
    trashNavigationPanelItems,
  };
}
